/**
 * TeamSelectionModal.js — Full-screen modal to pick a team from the account's
 * accessible list.
 *
 * Displays each team as a card with its four kit colours, name, player count,
 * and a "Shared" badge for teams owned by another account. The currently
 * selected team is highlighted with a cyan border.
 *
 * Role in the app: shown when the user clicks a team name in the account bar
 * or TeamMenu header (premium only). Calls `onSelect(teamId)` and closes.
 *
 * Props:
 * - teams:          array of team objects (from the backend /accounts/:id/teams)
 * - currentTeamId:  currently selected team _id (for highlight)
 * - onSelect:       (teamId) => void
 * - onClose:        () => void
 * - title:          string (e.g. "Select Home Team")
 * - onCreateTeam:   () => void — offered in the empty state (optional)
 */

import React from 'react';
import { useI18n } from './i18n';

/**
 * TeamSelectionModal component.
 *
 * @param {Object}   props
 * @param {Array}    [props.teams=[]]          - Teams to display.
 * @param {string}   [props.currentTeamId]     - _id of the currently selected team.
 * @param {Function} props.onSelect            - Called with the chosen teamId.
 * @param {Function} props.onClose             - Called to dismiss the modal.
 * @param {string}   [props.title]             - Modal heading; defaults to the
 *                                               dictionary's "Select a team".
 * @returns {JSX.Element} The modal markup.
 */
function TeamSelectionModal({ teams = [], currentTeamId, onSelect, onClose, title = null, onCreateTeam = null }) {
  const { t } = useI18n();
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div className="kv-selection-modal" style={modalStyle} onClick={e => e.stopPropagation()}>
        {/* ── Header: title + close button ── */}
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: '1.2rem' }}>{title || t('teams.pickTitle')}</h2>
          <button style={closeBtnStyle} onClick={onClose}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
        </div>
        {/* ── Team list (or empty-state message) ── */}
        <div style={listStyle}>
          {teams.length === 0 ? (
            // The old copy was a dead end that pushed you OUT of the app:
            // "Create teams from the dashboard." The app has a team editor —
            // so the empty state is a button, not a sentence about somewhere else.
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem 1rem' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.9rem' }}>{t('teams.none')}</div>
              {onCreateTeam && (
                <button
                  type="button"
                  onClick={onCreateTeam}
                  style={{
                    minHeight: 40, padding: '0 16px', borderRadius: 8, cursor: 'pointer',
                    background: '#16a34a', color: '#fff', border: 'none',
                    fontSize: '0.86rem', fontWeight: 700,
                  }}
                >{t('teams.new')}</button>
              )}
            </div>
          ) : (
            teams.map(team => (
              <div
                key={team._id}
                onClick={() => { onSelect(team._id); onClose(); }}
                style={{
                  ...cardStyle,
                  border: team._id === currentTeamId ? '2px solid #38bdf8' : '1px solid #334155',
                  background: team._id === currentTeamId ? '#0c4a6e' : '#0f172a',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {/* Four kit colour swatches: shirt, shorts, socks, shoes */}
                  <div style={{ display: 'flex', gap: '0.3rem' }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', background: team.color1, border: '1px solid #475569' }} />
                    <div style={{ width: 16, height: 16, borderRadius: '50%', background: team.color2, border: '1px solid #475569' }} />
                    <div style={{ width: 16, height: 16, borderRadius: '50%', background: team.color3, border: '1px solid #475569' }} />
                    <div style={{ width: 16, height: 16, borderRadius: '50%', background: team.color4 || '#000000', border: '1px solid #475569' }} />
                  </div>
                  <div style={{ minWidth:0, overflowWrap:'anywhere' }}>
                    <div style={{ fontWeight: 700, color: '#38bdf8' }}>{team.name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {t('teams.playersCount', { n: (team.playerInfo || []).length })}
                      {!team.isOwner && team.accountId ? ` · ${t('teams.shared')}` : ''}
                    </div>
                  </div>
                  {/* Checkmark on the currently selected team */}
                  {team._id === currentTeamId && (
                    <span style={{ marginLeft: 'auto', color: '#38bdf8', fontSize: '1.2rem' }}>✓</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ── Inline styles ──────────────────────────────────

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 9998,
  background: 'rgba(0,0,0,0.7)', display: 'flex',
  justifyContent: 'center', alignItems: 'center',
};

const modalStyle = {
  background: '#1e293b', borderRadius: '12px', padding: '1.5rem',
  width: '100%', maxWidth: '480px',
  // dvh-backed var: the visible viewport, so the modal never lands under the
  // mobile browser chrome in landscape
  maxHeight: 'calc(var(--kv-viewport-h) * 0.8)', overflow: 'hidden',
  display: 'flex', flexDirection: 'column',
};

const headerStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #334155',
};

const closeBtnStyle = {
  background: '#475569', border: 'none', borderRadius: '6px',
  color: '#fff', width: '30px', height: '30px', cursor: 'pointer', fontSize: '0.9rem',
};

const listStyle = {
  overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem',
};

const cardStyle = {
  padding: '0.75rem', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s',
};

export default TeamSelectionModal;
